import { ActionCreator, ActionType, Action, ActionReducer } from './models';

type ExtractActionTypes<Creators extends readonly ActionCreator[]> = {
  [Key in keyof Creators]: Creators[Key] extends ActionCreator<infer T>
    ? T
    : never;
};

export interface ReducerTypes<
  State,
  Creators extends readonly ActionCreator[]
> {
  reducer: OnReducer<State, Creators>;
  types: ExtractActionTypes<Creators>;
}

export interface OnReducer<
  State,
  Creators extends readonly ActionCreator[],
  InferredState = State,
  ResultState = unknown extends State ? InferredState : State
> {
  (
    state: unknown extends State ? InferredState : State,
    action: ActionType<Creators[number]>
  ): ResultState;
}

export function on<
  State,
  Creators extends readonly ActionCreator[],
  InferredState = State
>(
  ...args: [
    ...creators: Creators,
    reducer: OnReducer<
      State extends infer S ? S : never,
      Creators,
      InferredState
    >
  ]
): ReducerTypes<unknown extends State ? InferredState : State, Creators> {
  const reducer = args.pop() as unknown as OnReducer<
    unknown extends State ? InferredState : State,
    Creators
  >;
  const types = (args as unknown as Creators).map(
    creator => creator.type
  ) as unknown as ExtractActionTypes<Creators>;
  return { reducer, types };
}

export function createReducer<
  S,
  A extends Action = Action,
  R extends ActionReducer<S, A> = ActionReducer<S, A>
>(...ons: ReducerTypes<S, readonly ActionCreator[]>[]): R {
  const map = new Map<string, OnReducer<S, ActionCreator[]>>();
  for (const on of ons) {
    for (const type of on.types) {
      const existingReducer = map.get(type);
      if (existingReducer) {
        const newReducer: typeof existingReducer = (state, action) =>
          on.reducer(existingReducer(state, action), action);
        map.set(type, newReducer);
      } else {
        map.set(type, on.reducer);
      }
    }
  }

  return function (state: S, action: A): S {
    const reducer = map.get(action.type);
    return reducer ? reducer(state, action) : state;
  } as R;
}
